import type { ModalMap } from "../modal/modalTypes";
import useAPI from "../Hooks/useAPI";
import { useEffect, useState, useCallback } from "react";
import { useUser } from "../Context/UserContext";
import Button2 from "../Components/Button2/Button2";
import "./EngineDetailModal.css";
type Props = ModalMap["engineDetail"] & {
  closeModal: () => void;
};

interface Engine {
  id: number;
  manufacturer: string;
  model: string;
  displacement_l: number;
  bore_mm: number;
  stroke_mm: number;
  compression_ratio: number;
  torque_lbft: number;
  power_output_hp: number;
  max_turbocharger_compression_psi: number;
  number_of_cylinders: number;
  cylinder_configuration: string;
  fuel_type: string;
  valvetrain: string;
  aspiration: string;
  fuel_system: string;
  redline_rpm: number;
  cooling_system: string;
  dry_weight_lb: number;
  ignition_type: string;
  maximum_rpm: number;
  stroke_type: string;
  maximum_power_output_hp: number;
  continuous_power_output_hp: number;
  dimensions: string;
  emissions_us_tier: string;
  emissions_eu_stage: string;
  engine_application: string;
  created_at: string;
  updated_at: string;
  category: string;
  data_quality_flag: boolean;
  bsfc_rated_g_kwh: number;
  bsfc_peak_torque_g_kwh: number;
  bsfc_source: string;
  brake_thermal_efficiency: number;
  scavenging_type: string;
}

interface EngineImage {
  engineid: number;
  engine_image_filename: string;
  engine_cad_filename: string;
}

export function EngineDetailModal({ id, closeModal }: Props) {
  const [engine, setEngine] = useState<Engine[]>([]);
  const [engineImages, setEngineImages] = useState<EngineImage[]>([]);
  const { getEngines, getEngineImages } = useAPI();
  const { user } = useUser();
  const [noData, setNoData] = useState(false);
  const [noImages, setNoImages] = useState(false);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchEngines = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await getEngines(id, JSON.stringify(null));

      if (response?.data) {
        // setEngines(response.data);
        setNoData(false);

        setEngine(response.data);

        if (response?.data.length === 0) {
          setNoData(true);
        }
      } else {
        throw new Error("Failed to fetch engine");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchEngineImages = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await getEngineImages(id);

      if (response?.data) {
        // setEngines(response.data);
        setNoImages(false);

        setEngineImages(response.data);

        if (response?.data.length === 0) {
          setNoImages(true);
        }
      } else {
        throw new Error("Failed to fetch engine images");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEngines();
    fetchEngineImages();
  }, []);

  type EngineField = {
    label: string;
    key: keyof Engine;
  };

  const fields: EngineField[] = [
    { label: "Manufacturer", key: "manufacturer" },
    { label: "Model", key: "model" },
    { label: "Application", key: "engine_application" },
    { label: "Max Power Output (hp)", key: "maximum_power_output_hp" },
    {
      label: "Continuous Power Output (hp)",
      key: "continuous_power_output_hp",
    },
    { label: "Maximum RPM", key: "maximum_rpm" },

    { label: "Max Torque (lb-ft)", key: "torque_lbft" },
    { label: "Ignition Type", key: "ignition_type" },
    { label: "Bore (mm)", key: "bore_mm" },
    { label: "Stroke (mm)", key: "stroke_mm" },
    { label: "Stroke Type", key: "stroke_type" },
    { label: "Displacement (L)", key: "displacement_l" },
    { label: "Dry Weight (lb)", key: "dry_weight_lb" },
    { label: "Number of Cylinders", key: "number_of_cylinders" },
    { label: "Cylinder Layout", key: "cylinder_configuration" },
    { label: "Compression Ratio", key: "compression_ratio" },
    {
      label: "Max Turbocharger Compression (psi)",
      key: "max_turbocharger_compression_psi",
    },
    { label: "Brake Thermal Efficiency", key: "brake_thermal_efficiency" },
    { label: "Fuel System", key: "fuel_system" },
    { label: "Fuel Type", key: "fuel_type" },
    { label: "Scavenging Type", key: "scavenging_type" },
    { label: "Valvetrain", key: "valvetrain" },
    { label: "Aspiration", key: "aspiration" },
    { label: "Redline RPM", key: "redline_rpm" },
    { label: "Cooling System", key: "cooling_system" },
    { label: "Dimensions", key: "dimensions" },
    { label: "Emissions US Tier", key: "emissions_us_tier" },
    { label: "Emissions EU Stage", key: "emissions_eu_stage" },
    { label: "BSFC Rated (g/kWh)", key: "bsfc_rated_g_kwh" },
    { label: "BSFC at Peak Torque (g/kWh)", key: "bsfc_peak_torque_g_kwh" },
    { label: "BSFC Source", key: "bsfc_source" },
  ];

  function formatPercentage(value: number | string | null | undefined): string {
    if (value == null || isNaN(Number(value))) return "NA";
    return `${Math.round(Number(value))}%`;
  }

  const validImages = engineImages.filter((img) => img.engine_image_filename);

  return (
    <div className="modalOverlay">
      <div className="modalContent">
        <div className="modal-comparison-wrapper">
          <div className="modal-comparison-section">
            <div className="modal-engine-card modal-engine-display-container">
              <div className="engine-display-scroll">
                <div
                  className="modal-engine-display-grid"
                  style={{ ["--col-count" as any]: engine.length }}
                >
                  {/* EMPTY top-left */}
                  <div></div>

                  {/* COLUMN HEADERS */}
                  {engine.map((engine, i) => (
                    <div key={i}>
                      {/* Do not remove - it will break alignment */}
                    </div>
                  ))}

                  {/* ROWS (ALWAYS SHOW LABELS) */}
                  {fields.map((field, rowIndex) => (
                    <div key={rowIndex} style={{ display: "contents" }}>
                      {/* LABEL (always visible) */}
                      <div className="modal-engine-display-label">
                        <div className="modal-engine-display-label-inner">
                          {field.label}
                        </div>
                      </div>

                      {/* VALUES (only if selected engines exist) */}
                      {engine.length > 0 &&
                        engine.map((engine, colIndex) => {
                          const manufacturer =
                            engine.manufacturer?.toLowerCase() || "";
                          const fieldKey = field.key?.toLowerCase();

                          const isElf = manufacturer.includes("elf");

                          const isTitleField =
                            fieldKey === "manufacturer" || fieldKey === "model";

                          const value = engine?.[field.key];

                          return (
                            <div
                              key={colIndex}
                              className={`modal-engine-display-header
          ${isElf ? "modal-engine-display-header-elf-tech" : ""}
          ${!isTitleField ? "modal-engine-display-cell" : ""}
        `}
                            >
                              {fieldKey === "brake_thermal_efficiency" ? (
                                formatPercentage(
                                  engine?.brake_thermal_efficiency,
                                )
                              ) : fieldKey === "manufacturer" ? (
                                <>
                                  {value ?? "-"}
                                  {isElf && <sup>TM</sup>}
                                </>
                              ) : (
                                (value ?? "NA")
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ))}
                </div>
              </div>

              {validImages.length > 0 && (
                <div className="engine-display-images">
                  {validImages.map((img, index) => (
                    <div key={index} className="engine-image-container">
                      <img
                        src={"/Images/Engines/" + img.engine_image_filename}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="message-button">
              <Button2
                name="close"
                text="Close"
                type="button"
                classname="modal-submit-btn"
                onClick={() => {
                  closeModal();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
